import { createContext, forwardRef, useContext, useId, useState } from 'react'
import type {
  InputHTMLAttributes,
  LabelHTMLAttributes,
  ReactElement,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import { toLatinDigits, toPersianDigits } from '@metal/shared'
import { cx } from '../cx'
import { Icon } from '../Icon'

export function Label({
  required,
  className,
  children,
  ...rest
}: LabelHTMLAttributes<HTMLLabelElement> & { required?: boolean }) {
  return (
    <label className={cx('label', className)} {...rest}>
      {children}
      {required && <span className="req">*</span>}
    </label>
  )
}

export interface FieldProps {
  label?: ReactNode
  required?: boolean
  /** Carries data the user needs. Never an explanation of why the field exists. */
  hint?: ReactNode
  tip?: string
  error?: string
  htmlFor?: string
  className?: string
  children: ReactNode
}

/** The id the label points at, handed down rather than asked for. */
const FieldIdContext = createContext<string | undefined>(undefined)

/** The control's own id, or the one its field minted. */
export function useFieldId(explicit?: string): string | undefined {
  const inherited = useContext(FieldIdContext)
  return explicit ?? inherited
}

export function Field({ label, required, hint, tip, error, htmlFor, className, children }: FieldProps) {
  const auto = useId()
  const id = htmlFor ?? auto
  return (
    <div className={cx('field', className)}>
      {label &&
        (tip ? (
          <span className="label-row">
            <Label htmlFor={id} required={required}>
              {label}
            </Label>
            <InfoTip text={tip} />
          </span>
        ) : (
          <Label htmlFor={id} required={required}>
            {label}
          </Label>
        ))}
      <FieldIdContext.Provider value={id}>{children}</FieldIdContext.Provider>
      {error ? <ErrorText>{error}</ErrorText> : hint ? <Hint>{hint}</Hint> : null}
    </div>
  )
}

/** The «i» that defines a term. */
export function InfoTip({ text, className }: { text: string; className?: string }) {
  const id = useId()
  return (
    <span className={cx('infotip', className)}>
      <button type="button" className="infotip-btn" aria-describedby={id} aria-label="توضیح">
        <Icon name="info" size="sm" />
      </button>
      <span role="tooltip" id={id} className="infotip-bubble">
        {text}
      </span>
    </span>
  )
}

export function Hint({
  tone = 'muted',
  className,
  children,
}: {
  tone?: 'muted' | 'buy' | 'sell'
  className?: string
  children: ReactNode
}) {
  return (
    <div className={cx('hint', tone === 'buy' && 't-buy', tone === 'sell' && 't-sell', className)}>
      {children}
    </div>
  )
}

export function ErrorText({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx('err', className)} role="alert">
      <Icon name="alert" size="sm" />
      {children}
    </div>
  )
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean
  /** This field carries a number. */
  numeric?: boolean
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, numeric, className, onChange, id, ...rest },
  ref,
) {
  const fieldId = useFieldId(id)
  return (
    <input
      ref={ref}
      id={fieldId}
      className={cx('input', numeric && 'num', invalid && 'is-error', className)}
      aria-invalid={invalid || undefined}
      {...rest}
      onChange={
        numeric && onChange
          ? (e) => {
              const latin = toLatinDigits(e.target.value)
              if (latin !== e.target.value) e.target.value = latin
              onChange(e)
            }
          : onChange
      }
    />
  )
})

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  invalid?: boolean
  options: SelectOption[]
  placeholder?: string
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { invalid, options, placeholder, className, id, ...rest },
  ref,
) {
  const fieldId = useFieldId(id)
  return (
    <div className="select-wrap">
      <select
        ref={ref}
        id={fieldId}
        className={cx('select', invalid && 'is-error', className)}
        aria-invalid={invalid || undefined}
        {...rest}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
      <Icon name="chevron-down" size="sm" />
    </div>
  )
})

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }
>(function Textarea({ invalid, className, id, ...rest }, ref) {
  const fieldId = useFieldId(id)
  return (
    <textarea
      ref={ref}
      id={fieldId}
      className={cx('textarea', invalid && 'is-error', className)}
      aria-invalid={invalid || undefined}
      {...rest}
    />
  )
})

/** The unit lives inside the control, never beside it: ریال, کیلوگرم, درصد. */
export function Affix({ tag, children }: { tag: string; children: ReactElement }) {
  return (
    <div className="affix">
      {children}
      <span className="affix-tag">{tag}</span>
    </div>
  )
}

export function Check({
  checked,
  onChange,
  disabled,
  children,
  className,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
  children?: ReactNode
  className?: string
}) {
  return (
    <label
      className={cx('check', checked && 'is-on', className)}
      aria-disabled={disabled || undefined}
    >
      <input
        type="checkbox"
        className="visually-hidden"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="box">
        <Icon name="check" />
      </span>
      {children}
    </label>
  )
}

export function Radio({
  checked,
  onChange,
  name,
  value,
  disabled,
  children,
  className,
}: {
  checked: boolean
  onChange: (v: string) => void
  name: string
  value: string
  disabled?: boolean
  children?: ReactNode
  className?: string
}) {
  return (
    <label
      className={cx('radio', checked && 'is-on', className)}
      aria-disabled={disabled || undefined}
    >
      <input
        type="radio"
        className="visually-hidden"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={() => onChange(value)}
      />
      <span className="box" />
      {children}
    </label>
  )
}

export function Switch({
  checked,
  onChange,
  disabled,
  label,
  className,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
  label: string
  className?: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className={cx('switch', checked && 'is-on', className)}
      onClick={() => onChange(!checked)}
    />
  )
}

export function FieldGroup({
  cols = 4,
  className,
  children,
}: {
  cols?: 2 | 3 | 4
  className?: string
  children: ReactNode
}) {
  return (
    <div className={cx('fgrid', `fgrid-${cols}`, className)} data-cols={cols}>
      {children}
    </div>
  )
}

/** Six boxes, one digit each. ID-032 fixes six digits and a 120 second expiry. */
/** The six box code. */
export function Otp({
  length = 6,
  value,
  onChange,
  invalid,
  autoFocus,
}: {
  length?: number
  value: string
  onChange: (v: string) => void
  invalid?: boolean
  autoFocus?: boolean
}) {
  const id = useId()
  const [slots, setSlots] = useState<string[]>(() => spread(value, length))

  // The parent clears the code after a wrong one, and a resend mints a new // challenge.
  const emitted = slots.filter(Boolean).join('')
  if (value !== emitted) {
    setSlots(spread(value, length))
  }

  const write = (next: string[]) => {
    setSlots(next)
    onChange(next.filter(Boolean).join(''))
  }

  const boxAt = (el: HTMLElement, index: number): HTMLInputElement | undefined =>
    el.parentElement?.children[index] as HTMLInputElement | undefined

  return (
    <div className={cx('otp', invalid && 'is-error')} dir="ltr">
      {slots.map((d, i) => (
        <input
          key={`${id}-${i}`}
          className={cx('otp-box', d && 'is-on')}
          inputMode="numeric"
          maxLength={1}
          autoFocus={autoFocus && i === 0}
          value={d}
          aria-label={`رقم ${toPersianDigits(i + 1)}`}
          onChange={(e) => {
            const raw = toLatinDigits(e.target.value).replace(/\D/g, '').slice(-1)
            write(slots.map((slot, n) => (n === i ? raw : slot)))
            if (raw) boxAt(e.target, i + 1)?.focus()
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Backspace') return
            // A filled box empties in place.
            if (d) return
            e.preventDefault()
            write(slots.map((slot, n) => (n === i - 1 ? '' : slot)))
            boxAt(e.target as HTMLElement, i - 1)?.focus()
          }}
          onPaste={(e) => {
            e.preventDefault()
            const pasted = toLatinDigits(e.clipboardData.getData('text'))
              .replace(/\D/g, '')
              .slice(0, length)
            if (pasted) write(spread(pasted, length))
          }}
        />
      ))}
    </div>
  )
}

/** A code as one box per character, padded to the full width. */
function spread(code: string, length: number): string[] {
  return Array.from({ length }, (_, i) => code[i] ?? '')
}
